import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Box,
  Button,
  Container,
  CircularProgress,
  TableContainer,
  TableSortLabel,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Link
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

import AddIcon from "@mui/icons-material/Add";
import DraftsIcon from "@mui/icons-material/Drafts";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ButtonGroup from "@mui/material/ButtonGroup";
import { actionButtonStyles } from "../styles/actionButton";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import axios from "../../src/utils/axiosConfig"
import { formatDate } from "../utils/dateUtils";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AttachFileIcon from "@mui/icons-material/AttachFile";


const breadcrumbs = [{ label: "Home", href: "/" }, { label: "Emails" }];

const EmailPage = () => {
  const { t } = useTranslation(['pages', 'common', 'buttons']);
  const navigate = useNavigate();
  const tableStyles = windowsTableStyles();

  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [showDrafts, setShowDrafts] = useState(false);
  const [openDocsModal, setOpenDocsModal] = useState(false);
  const [modalDocs, setModalDocs] = useState([]);
  const [modalEmailId, setModalEmailId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dropdownDocs, setDropdownDocs] = useState([]);
  const [dropdownEmailId, setDropdownEmailId] = useState(null);


  useEffect(() => {
    const fetchEmails = async () => {
      setLoading(true);
      try {
        const res = await axios.get(showDrafts ? "/api/email/drafts" : "/api/email/list");
        console.log("res", res);
        let emailData = res.data || [];
        // Filter out drafts when showing sent emails
        if (!showDrafts) {
          emailData = emailData.filter(email => !email.isDraft);
        }
        // Flatten emails so each recipient gets its own row
        let flattened = [];
        emailData.forEach(email => {
          const recipients = Array.isArray(email.recipient) ? email.recipient : [];
          const manualRecipients = Array.isArray(email.manualRecipient) ? email.manualRecipient : [];
          if (recipients.length === 0 && manualRecipients.length === 0) {
            // No recipients, still show the row
            flattened.push({ ...email, _rowRecipient: null });
          } else {
            recipients.forEach(rec => {
              flattened.push({ ...email, _rowRecipient: rec });
            });
            manualRecipients.forEach(m => {
              flattened.push({ ...email, _rowRecipient: { email: m, _id: 'manual' } });
            });
          }
        });
        setEmails(flattened);
      } catch (err) {
        console.error("Failed to fetch emails:", err);
        setEmails([]);
      }
      setLoading(false);
    };
    fetchEmails();
  }, [showDrafts]);

  console.log("emails are", emails)


  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleOpenDraft = (draft) => {
    navigate(`/send-email?draftId=${draft._id}`);
  };

  // Placeholder data for the table
  // const emails = [
  //   {
  //     id: 1,
  //     from: "john.doe@example.com",
  //     recipient: "jane.doe@example.com",
  //     date: "2023-05-01",
  //     document: "Invoice.pdf",
  //     subject: "Invoice for April",
  //   },
  //   {
  //     id: 2,
  //     from: "admin@example.com",
  //     recipient: "user@example.com",
  //     date: "2023-05-02",
  //     document: "Report.docx",
  //     subject: "Monthly Report",
  //   },
  //   {
  //     id: 3,
  //     from: "support@example.com",
  //     recipient: "client@example.com",
  //     date: "2023-05-03",
  //     document: "SupportTicket.pdf",
  //     subject: "Support Ticket Update",
  //   },
  // ];

  const sortedEmails = [...emails].sort((a, b) => {
    if (!orderBy) return 0;
    let aValue = a[orderBy];
    let bValue = b[orderBy];
    if (orderBy === "date" && a.createdAt && b.createdAt) {
      aValue = a.createdAt;
      bValue = b.createdAt;
    }
    if (aValue < bValue) return order === "asc" ? -1 : 1;
    if (aValue > bValue) return order === "asc" ? 1 : -1;
    return 0;
  });

  console.log("sortedEmails", sortedEmails);

  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: "white",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.013)",
        pt: 2,
        marginBottom: 8,
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          mt: 2,
        }}
      >
        <BreadcrumbNav title={showDrafts ? t('pages:emails.drafts') : t('pages:emails.sent')} breadcrumbs={breadcrumbs} />
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="text"
            onClick={() => setShowDrafts((prev) => !prev)}
            sx={{
              textTransform: "none",
              color: "#2B80B6 !important",
              fontWeight: 500,
              padding: "6px 20px",
              fontSize: "13px",
              borderRadius: "0",
              fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
              marginBottom: 4
            }}
            startIcon={showDrafts ? <MailOutlineIcon /> : <EditNoteIcon />}
            size="small"
            color={showDrafts ? "secondary" : "primary"}
          >
            {showDrafts ? t('pages:emails.show_sent') : t('pages:emails.show_drafts')}
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={() => navigate("/send-email")}
            size="small"
            sx={{
              ...actionButtonStyles(),
              marginBottom: 4
            }}
          >
            {t('pages:emails.compose')}
          </Button>
        </Box>
      </Box>
      <Box>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "32px 24px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table sx={tableStyles.container} size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={tableStyles.headerCell}>#</TableCell>
                  <TableCell sx={tableStyles.headerCell}>
                    <TableSortLabel
                      active={orderBy === "from"}
                      direction={orderBy === "from" ? order : "asc"}
                      onClick={() => handleRequestSort("from")}
                    >
                      {t('pages:emails.from')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={tableStyles.headerCell}>
                    <TableSortLabel
                      active={orderBy === "recipient"}
                      direction={orderBy === "recipient" ? order : "asc"}
                      onClick={() => handleRequestSort("recipient")}
                    >
                      {t('pages:emails.recipient')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={tableStyles.headerCell}>
                    {t('common:case')}
                  </TableCell>
                  <TableCell sx={tableStyles.headerCell}>
                    <TableSortLabel
                      active={orderBy === "date"}
                      direction={orderBy === "date" ? order : "asc"}
                      onClick={() => handleRequestSort("date")}
                    >
                      {t('forms:labels.date')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={tableStyles.headerCell}>{t('common:document')}</TableCell>
                  <TableCell sx={tableStyles.headerCell}>
                    <TableSortLabel
                      active={orderBy === "subject"}
                      direction={orderBy === "subject" ? order : "asc"}
                      onClick={() => handleRequestSort("subject")}
                    >
                      {t('forms:labels.subject')}
                    </TableSortLabel>
                  </TableCell>
                  {showDrafts && (
                    <TableCell sx={tableStyles.headerCell}>{t('pages:emails.draft')}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedEmails
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((email, index) => (
                    <TableRow
                      sx={{
                        ...tableStyles.cell,
                        ...(email.isDraft && {
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            transition: 'background-color 0.2s ease'
                          }
                        })
                      }}
                      key={email._id}
                      onClick={() => {
                        if (email.isDraft) {
                          handleOpenDraft(email);
                        }
                      }}
                      style={{ cursor: email.isDraft ? "pointer" : "default" }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar
                            src={email.from?.avatar}
                            alt={
                              email.from?.businessName
                                ? email.from.businessName
                                : email.from?.firstName || email.from?.email || ""
                            }
                            sx={{ width: "1rem", height: "1rem" }}
                          >
                            {email.from?.businessName
                              ? email.from.businessName[0]
                              : email.from?.firstName
                                ? email.from.firstName[0]
                                : (email.from?.email || "?")[0]}
                          </Avatar>
                          <span>
                            {email.from?.businessName
                              ? email.from.businessName
                              : email.from?.firstName
                                ? `${email.from.firstName} ${email.from.lastName || ""}`
                                : email.from?.email || email.from || ""}
                          </span>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                          {email._rowRecipient ? (
                            <>
                              <Avatar
                                src={email._rowRecipient.avatar}
                                alt={email._rowRecipient.businessName || email._rowRecipient.firstName || email._rowRecipient.email || email._rowRecipient || ""}
                                sx={{
                                  width: "1rem",
                                  height: "1rem",
                                  bgcolor: email._rowRecipient._id === 'manual' ? '#757575' : undefined,
                                  fontSize: email._rowRecipient._id === 'manual' ? '0.5rem' : '0.75rem'
                                }}
                              >
                                {email._rowRecipient.businessName
                                  ? email._rowRecipient.businessName[0]
                                  : email._rowRecipient.firstName
                                    ? email._rowRecipient.firstName[0]
                                    : (email._rowRecipient.email || email._rowRecipient || "?")[0]}
                              </Avatar>
                              <span>
                                {email._rowRecipient.businessName
                                  ? email._rowRecipient.businessName
                                  : email._rowRecipient.firstName
                                    ? `${email._rowRecipient.firstName} ${email._rowRecipient.lastName || ""}`
                                    : email._rowRecipient.email || email._rowRecipient || ""}
                              </span>
                            </>
                          ) : (
                            <span style={{ color: '#aaa' }}>-</span>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>
                        {console.log("email", email)}
                        {email?.caseId ? (
                          <Link
                            component={RouterLink}
                            to={`/files/${email.caseId._id || email.caseId}`}
                            underline="hover"
                            color="primary"
                            sx={{ cursor: "pointer", fontWeight: 500 }}
                            onClick={e => e.stopPropagation()}
                          >
                            {email?.caseId?.fileNo && email?.caseId?.name
                              ? `${email?.caseId?.fileNo} - ${email?.caseId?.name}`
                              : email?.caseId?.fileNo || email?.caseId?.name || email?.caseId?._id || email?.caseId}
                          </Link>
                        ) : (
                          <span style={{ color: '#aaa' }}>-</span>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>
                        {email.createdAt ? formatDate(email.createdAt) : ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableStyles.cell,
                          // "&:hover": {
                          //   textDecoration: "underline",
                          //   cursor: "pointer",
                          // },
                          color: "#005b92",
                          lineHeight: "12px",
                        }}
                      >
                        {/* 'View all' link for multiple documents */}
                        {Array.isArray(email.documentName) && email.documentName.length > 1 ? (
                          <>
                            <span
                              style={{ color: "#005b92", textDecoration: "underline", cursor: "pointer" }}
                              onClick={e => {
                                setAnchorEl(e.currentTarget);
                                setDropdownDocs(email.documentName.map((name, idx) => ({ name, idx })));
                                setDropdownEmailId(email._id);
                              }}
                            >
                              View all
                            </span>
                            <Menu
                              anchorEl={anchorEl}
                              open={Boolean(anchorEl) && dropdownEmailId === email._id}
                              onClose={() => setAnchorEl(null)}
                              PaperProps={{
                                style: {
                                  maxHeight: 200,
                                  width: 250,
                                  overflowY: 'auto',
                                },
                              }}
                            >
                              {dropdownDocs.map(doc => (
                                <MenuItem
                                  key={doc.name + doc.idx}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setAnchorEl(null);
                                    try {
                                      const response = await axios.get(`/api/email/document/${dropdownEmailId}?docIdx=${doc.idx}`, {
                                        responseType: "blob",
                                      });
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement("a");
                                      link.href = url;
                                      link.setAttribute("download", doc.name);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.parentNode.removeChild(link);
                                      window.URL.revokeObjectURL(url);
                                    } catch (err) {
                                      alert("Failed to download document.");
                                    }
                                  }}
                                >
                                  <Tooltip title={doc.name} placement="right">
                                    <span style={{
                                      display: 'inline-block',
                                      maxWidth: 180,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      verticalAlign: 'middle',
                                    }}>{doc.name}</span>
                                  </Tooltip>
                                </MenuItem>
                              ))}
                            </Menu>
                          </>
                        ) : Array.isArray(email.documentName) && email.documentName.length === 1 ? (
                          <span
                            style={{ color: "#005b92", textDecoration: "underline", cursor: "pointer" }}
                            onClick={async (e) => {
                              e.preventDefault();
                              try {
                                const response = await axios.get(`/api/email/document/${email._id}?docIdx=0`, {
                                  responseType: "blob",
                                });
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement("a");
                                link.href = url;
                                link.setAttribute("download", email.documentName[0]);
                                document.body.appendChild(link);
                                link.click();
                                link.parentNode.removeChild(link);
                                window.URL.revokeObjectURL(url);
                              } catch (err) {
                                alert("Failed to download document.");
                              }
                            }}
                          >
                            {email.documentName[0]}
                          </span>
                        ) : email.documentName ? (
                          <span
                            style={{ color: "#005b92", textDecoration: "underline", cursor: "pointer" }}
                            onClick={async (e) => {
                              e.preventDefault();
                              try {
                                const response = await axios.get(`/api/email/document/${email._id}`, {
                                  responseType: "blob",
                                });
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement("a");
                                link.href = url;
                                link.setAttribute("download", email.documentName);
                                document.body.appendChild(link);
                                link.click();
                                link.parentNode.removeChild(link);
                                window.URL.revokeObjectURL(url);
                              } catch (err) {
                                alert("Failed to download document.");
                              }
                            }}
                          >
                            {email.documentName}
                          </span>
                        ) : (
                          <span style={{ color: "#222", fontWeight: 500, textDecoration: "none", cursor: "default" }}>N/A</span>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.cell }}>{email.subject}</TableCell>
                      {showDrafts && (
                        <TableCell>
                          {email.isDraft ? t('pages:emails.draft') : ""}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={emails.length}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage={t('common:rows_per_page')}
              sx={{ borderTop: "1px solid #ddd", padding: "10px" }}
            />
          </TableContainer>
        )}
      </Box>
    </Container>

  );
};

export default EmailPage;